import { getExcelValuesConverter, bypassConverter } from 'shared/excel/converters';
import * as QA from 'shared/utils/queryAnalysis';
import { tryParseSubstitution, loadSubstitutions, replaceByValues, SubstitutionsError } from 'shared/utils/substitution';
import { MultipleErrorsError } from 'shared/utils/utils';
import { areStringsInsensitivelyEqual } from 'shared/utils/utils';


// If we have columns headers [a, b, c, d, e] and only [b, d] enabled, then
// indexes = [1, 3] - will be used to fetch the data from table
// mapping of b has columnIndex 1 in then headers and dataIndex 0 (pos of columnIndex in indexes)
// mappings of d has columnIndex 3 in the headers and dataIndex 1
// dataColumnIndexes are used to fetch right column in the data: 
// see processRow os applyInsertMapping and applyLeadConversion
export function mapDataColumnIndexes(indexes, loadedMapping) {
  loadedMapping.forEach(mappedColumn => {
    const { columnIndex } = mappedColumn.columnData;
    mappedColumn.dataColumnIndex = indexes.findIndex(x => x === columnIndex);
  });
}

export async function loadColumns(excelHeaders, { columnIndexes, objectName, columnConversion }, { namesConverter, loaders }) {
  const { fields: rawFields } = await loaders.objectLoader(objectName);
  const columns = [];

  for (const [fieldName, columnIndex] of Object.entries(columnIndexes)) {
    if (columnIndex === null) {
      continue;
    }

    const count = excelHeaders.length;
    if (columnIndex > count - 1) {
      throw new Error(`Column '${fieldName}' was mapped to the column #${columnIndex + 1}, but now the table has only ${count} column(s). Open your flowstep, verify mappings and try again`)
    }

    const fieldToFind = columnConversion[fieldName] ?? fieldName;
    const rawField = rawFields.find(x => areStringsInsensitivelyEqual(x.name, fieldToFind));
    
    // console.log('RawField: ', rawField, rawFields, fieldName, fieldToFind);
    const columnName = excelHeaders[columnIndex];
    const columnData = { columnName, columnIndex, rawField, objectName, fieldName };
    const converter = QA.isLookupField(rawField) 
      ? await namesConverter.getConverter(columnData)
      : getExcelValuesConverter(rawField);

    columns.push({ columnData, converter });
  }

  return columns;
}

export function applyMappedColumnToRow(row, rowIndex, mappedColumn) {
  const { columnData, converter, dataColumnIndex } = mappedColumn;
  const { columnIndex } = columnData;
  
  const value = row[dataColumnIndex];
  const { value: convertedValue, error } = converter(value, rowIndex);
    
  return error ? { error: { error, columnIndex }} : { value: convertedValue };
};

export async function applyMapping(excelHeaders, mapping, ctx) {
  const toIgnore = getTargetsToIgnore(mapping, ctx);
  const newCtx = { ...ctx, toIgnore };
  const mappingRes = await loadMapping(excelHeaders, mapping, newCtx);
  const constantsRes = await loadConstants(mapping, newCtx);
  
  const res = { ...constantsRes, ...mappingRes, ignoreAllExceptId: toIgnore.allExceptId };
  return res;
}

function isIdField(name) {
  return areStringsInsensitivelyEqual(name ?? '', QA.KnownFields.Id);
}

function getTargetsToIgnore(mapping, ctx) {
  const { indexesToIgnore: arrayOfIndexesToIgnore = [], ignoreAllIfIdExists = false } = ctx;
  const { mappedHeaders, constants } = mapping;

  const indexes = new Set(arrayOfIndexesToIgnore);
  let allExceptId = false;

  if (ignoreAllIfIdExists) {
    const mappingIndex = mappedHeaders.findIndex((x, index) => !indexes.has(index) && x.isUsed && isIdField(x.sfField?.name));
    const constantIndex = constants.findIndex(x => isIdField(x.field.name));

    if (mappingIndex !== -1 || constantIndex !== -1) {
      allExceptId = true;
      for (let i = 0; i < mappedHeaders.length; ++i) {
        indexes.add(i);
      }
      indexes.delete(mappingIndex);
    }
  }
  
  return { indexes, allExceptId, isIdField };
}

async function loadMapping(excelHeaders, mappingParams, { namesConverter, loaders, toIgnore }) {
  const { mappedHeaders, columnNameForResultIds = null, sfObjectName: objectName } = mappingParams;

  const count = mappedHeaders.length;
  // Special case for Merge and Convert Leads where mapping is optional
  // It's easier to handle it here to avoid copy-paste
  if (!count) {
    return { mapping: [], upsertRelationships: [], objectName };
  }

  const headersCount = excelHeaders.length;
  if (headersCount !== count) {
    throw new Error(`Change in data structure detected. There were ${count} data columns when you created this flow step, but now there are ${excelHeaders.length}. Please revisit your mappings.`);
  }

  let columnIndexForResultIds = null;
  if (columnNameForResultIds) {
    columnIndexForResultIds = mappedHeaders.findIndex(x => x.excelColumnName === columnNameForResultIds);
    if (-1 === columnIndexForResultIds)  { 
      throw new Error(`Column for result's IDs with name '${columnNameForResultIds}' wasn't found`);
    }
  }
 
  const mapping = [];
  let objectData = null;

  for (let columnIndex = 0; columnIndex < headersCount; ++columnIndex) {
    const m = mappedHeaders[columnIndex]
    const { sfField, isUsed, lookupObject } = m;
    if (!isUsed || !sfField || toIgnore.indexes.has(columnIndex)) {
      continue;
    }

    // Don't move this outside the loop: in case of convert leads
    // the mappings might be completely empty, so there is no need to load the object at all
    objectData ??= await loaders.objectLoader(objectName);
    const { fields: objectFields } = objectData;

    const { upsertRelationship } = sfField;
    // TODO: Check for field existence in case of upsertRelationship
    const rawField = upsertRelationship ? sfField : objectFields.find(x => x.name === sfField.name);
    const objName = upsertRelationship?.refObjectName ?? objectName;
    const columnName = excelHeaders[columnIndex];
    const columnData = { columnIndex, columnName, rawField, objectName: objName, upsertRelationship, lookupObject };
    const converter = QA.isLookupField(rawField) 
      ? await namesConverter.getConverter(columnData)
      : getExcelValuesConverter(rawField);

    // const [{ excelColumnIndex, excelColumnName }] = headers.splice(index, 1); // Exclude this entry from search
    mapping.push({ converter, columnData });
  }

  // throw new Error('Test apply');

  const upsertRelationships = mapping.map(x => x.columnData.upsertRelationship).filter(Boolean);
  return { mapping, columnIndexForResultIds, upsertRelationships, objectName };
}


async function loadConstants(mapping, ctx) {
  const { constants = [] } = mapping;

  const loaded = await resolveConstantSubstitutions(constants, ctx);
  const converted = await convertConstantValues(loaded, ctx);

  return {
    constants: converted.map(constant => {
      const { field: { name: fieldName, type: fieldType }, value } = constant;
      return { fieldName, fieldType, value };
    })
  }
}

async function resolveConstantSubstitutions(constants, ctx) {
  const { loaders, toIgnore } = ctx;
  const toLoad = [];

  const result = constants.reduce((out, constant, index) => {
    const { field: { name: fieldName, type: fieldType }, value } = constant;
    if (toIgnore.allExceptId && !toIgnore.isIdField(fieldName)) {
      return out;
    }

    const { substitutions, hasSubstitutions } = tryParseSubstitution(value);
    const v = hasSubstitutions
      ? { substitutions, value, fieldName, index, constant }
      : constant;
    hasSubstitutions && toLoad.push(v);
    out.push(v);
    return out;
  }, []);

  if (!toLoad.length || !result.length) {
    return result;
  }

  const expressions = toLoad.flatMap(x => x.substitutions.map(y => y.expression));
  const res = await loadSubstitutions(expressions, loaders);

  const errors = toLoad.reduce((out, { substitutions, value, fieldName, index, constant }) => {
    const resolvedSubstitutions = res.splice(0, substitutions.length);
    // console.log('resolveConstantSubstitutions: ', resolvedSubstitutions);

    // const fieldErrors = resolvedSubstitutions.reduce((ret, { error }) => {
    //   error && ret.push(error);
    //   return ret;
    // }, []);

    const fieldErrors = [];
    const substitutionValues = resolvedSubstitutions.map(({ range, error }, substitutionIndex) => {
      if (error) {
        fieldErrors.push(error);
        return {};
      }

      // const { value, error: valueError } = convertValue(range[0]);
      const { value, error: valueError } = range[0];
      if (valueError) {
        out.push(valueError);
        return {};
      }

      const { index, length } = substitutions[substitutionIndex];
      return { index, length, value };
    });

    if (fieldErrors.length) {
      const joinedErrors = fieldErrors.join('. ');
      out.push(`Unable to resolve substitution(s) for a constant assigned to the field '${fieldName}': ${joinedErrors}`);
      return out;
    }

    const finalValue = replaceByValues(value, substitutionValues);
    // We guarantee, that all constants which are not substituion have correct format
    result[index] = { ...constant, value: finalValue, needConversion: true };
    return out;
  }, []);

  if (errors.length) {
    throw new SubstitutionsError(errors);
  }

  return result;
}

async function convertConstantValues(constants, { loaders, namesConverter }) {
  const { objectLoader } = loaders;
  
  const res = [];
  const errors = [];

  for (let index = 0; index < constants.length; ++index ) {
    const constant = constants[index];
    const { field: { name }, objectName, value: initialValue, needConversion } = constant;
    const { fields } = await objectLoader(objectName);
    const rawField = fields.find(x => x.name === name);

    const columnData = { rawField, objectName, columnIndex: index };
    const converter = needConversion 
      ? QA.isLookupField(rawField)
        ? await namesConverter.getConverter(columnData)
        : getExcelValuesConverter(rawField)
      : bypassConverter;
    const { value, error } = converter(initialValue, index);
    
    if (error) {
      errors.push(`Unable to convert a constant assigned to the field '${name}'. ${error}`);
    }
    else {
      res.push({ ...constant, value });
    }

  }

  if (errors.length) {
    throw new MultipleErrorsError(errors, 'Unable to convert constants');
  }

  if (!namesConverter.isEmpty()) {
    const queryRes = await namesConverter.queryNames();
    const { toUpload, toReject } = queryRes; 
    if (toReject.length) {
      const errors = toReject.map(x => {
        const { rowIndex } = x;
        const { field: { name } } = res[rowIndex];
        return `Unable to convert name to id for a constant assigned to the field '${name}'. ${x.error}`
      });

      throw new MultipleErrorsError(errors, 'Unable to convert constants name(s) to id(s)');
    }

    toUpload.forEach(x => {
      const { rowIndex, columns } = x;
      const { value } = columns[0];
      Object.assign(res[rowIndex], { value });
    });
  }

  return res;
}

